<template>
  <div class="sound-tag-group-picker">
    <el-input
      v-model.trim="keyword"
      clearable
      size="small"
      prefix-icon="el-icon-search"
      :placeholder="`在“${group.name}”中搜索标签`"
    ></el-input>

    <div v-if="visibleTags.length" class="tag-choice-grid">
      <button
        v-for="tag in visibleTags"
        :key="tag.id"
        type="button"
        class="tag-choice"
        :class="{ selected: isSelected(tag.id), disabled: Number(tag.status) !== 1 }"
        :disabled="disabled || (Number(tag.status) !== 1 && !isSelected(tag.id))"
        @click="toggleTag(tag)"
      >
        <i :class="isSelected(tag.id) ? 'el-icon-check' : 'el-icon-plus'"></i>
        <span>
          <small v-if="tag.parent_name">{{ tag.parent_name }} ›</small>
          {{ tag.name }}
        </span>
      </button>
    </div>
    <el-empty v-else :image-size="60" description="本组没有匹配的标签"></el-empty>

    <div v-if="allowCreate" class="create-tag-row">
      <span>没有合适的标签？可以直接在这里创建。</span>
      <el-button
        type="text"
        icon="el-icon-circle-plus-outline"
        :disabled="disabled"
        @click="$emit('create', { group, keyword })"
      >
        新建“{{ group.name }}”标签
      </el-button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SoundTagGroupPicker',
  props: {
    group: {
      type: Object,
      required: true
    },
    value: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    allowCreate: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      keyword: ''
    }
  },
  computed: {
    /**
     * 将已选标签转换成数字集合，供筛选和点击判断复用。
     *
     * @returns {Set<number>}
     */
    selectedSet() {
      return new Set(this.value.map(Number))
    },
    /**
     * 按状态、名称、父标签和别名过滤当前组的可选标签。
     *
     * 已经选中的停用标签仍会显示，避免编辑页面静默丢失历史关系。
     *
     * @returns {Array}
     */
    visibleTags() {
      const keyword = this.keyword.toLowerCase()
      return (this.group.tags || []).filter(tag => {
        if (Number(tag.status) !== 1 && !this.selectedSet.has(Number(tag.id))) return false
        if (!keyword) return true
        return [
          tag.name,
          tag.parent_name || '',
          ...(tag.alias_names || [])
        ].join(' ').toLowerCase().includes(keyword)
      })
    }
  },
  watch: {
    /**
     * 切换标签组时清空组内搜索词，避免上一组条件误导用户。
     *
     * @returns {void}
     */
    'group.id'() {
      this.keyword = ''
    }
  },
  methods: {
    /**
     * 判断某个标签是否已经选中。
     *
     * @param {number|string} tagId 标签 ID
     * @returns {boolean}
     */
    isSelected(tagId) {
      return this.selectedSet.has(Number(tagId))
    },
    /**
     * 切换标签选择。
     *
     * 单选组会先移除同组已有标签，多选组只切换当前标签；最终通过 v-model
     * 的 input 事件返回去重后的完整标签 ID 数组。
     *
     * @param {Object} tag 被点击的标签
     * @returns {void}
     */
    toggleTag(tag) {
      const tagId = Number(tag.id)
      let next = this.value.map(Number)
      if (this.isSelected(tagId)) {
        next = next.filter(id => id !== tagId)
      } else {
        if (Number(this.group.allow_multiple) !== 1) {
          const groupIds = new Set((this.group.tags || []).map(item => Number(item.id)))
          next = next.filter(id => !groupIds.has(id))
        }
        next.push(tagId)
      }
      this.$emit('input', Array.from(new Set(next)))
    }
  }
}
</script>

<style lang="scss" scoped>
.sound-tag-group-picker {
  .el-input {
    max-width: 420px;
    margin-bottom: 12px;
  }
}

.tag-choice-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.create-tag-row {
  display: flex;
  margin-top: 12px;
  padding-top: 10px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-top: 1px dashed #dce3ed;
  color: #8a96a8;
  font-size: 12px;
}

.tag-choice {
  display: inline-flex;
  min-height: 34px;
  padding: 6px 11px;
  align-items: center;
  gap: 6px;
  border: 1px solid #dce3ed;
  border-radius: 8px;
  background: #fff;
  color: #4a5870;
  cursor: pointer;
  transition: 0.16s ease;

  &:hover {
    border-color: #9bb8ff;
    color: #356df3;
  }
  &.selected {
    border-color: #8badff;
    background: #edf3ff;
    color: #285fdc;
    box-shadow: inset 0 0 0 1px rgba(53, 109, 243, 0.08);
  }
  &.disabled {
    opacity: 0.58;
    cursor: not-allowed;
  }
  i { font-size: 12px; }
  small { color: #8a96a8; font-size: 11px; }
}
</style>
